// /src/hooks/useMediaQuery.js
// Custom hook for responsive breakpoint detection
// Used throughout the app for mobile/tablet/desktop conditional rendering
// Updated Jan 2026: Added dynamic sidebar compression for 1200-1400px range

import { useState, useEffect, useLayoutEffect } from 'react';

// Breakpoint definitions
const BREAKPOINTS = {
  mobile: 768,    // 0-767px = mobile
  tablet: 1024,   // 768-1023px = tablet, 1024px+ = desktop
  sidebarCompressStart: 1200,  // Start compressing sidebar at this width
  sidebarCompressEnd: 1400     // Full sidebar width at this width and above
};

// Sidebar width constants
const SIDEBAR_MIN_WIDTH = 384;  // 20% compression from 480px
const SIDEBAR_MAX_WIDTH = 480;  // Full width

// Fixed-chrome heights (LAYOUT.md § Header System / Footer System), verified against the doc,
// not hardcoded independently per page. Desktop is uniform across feed/detail/form (single
// 80px row). Mobile genuinely differs by page type: feed pages carry a two-row header (Row 1
// 50px + Row 2 40px = 90px); detail/form pages are a single 50px row. Footer/bottom-bar is
// 60px mobile either way, 120px desktop. This is the single source for "viewport minus fixed
// chrome" — only the OUTERMOST container on a page (the one that genuinely sits against
// HeaderContainer/FooterContainer) should read these; everything mounted inside that container
// should inherit height via flex/height:100%, not recompute against the viewport itself.
const CHROME = {
  desktop: { header: 80, footer: 120 },
  mobileFeed: { header: 90, footer: 60 },
  mobileDetail: { header: 50, footer: 60 }
};

/**
 * Calculate sidebar width based on viewport
 * @param {number} viewportWidth - Current viewport width
 * @returns {number} Sidebar width in pixels (0 for mobile, 384-480 for desktop)
 */
const calculateSidebarWidth = (viewportWidth) => {
  if (viewportWidth < BREAKPOINTS.sidebarCompressStart) {
    return 0; // Mobile interface - no sidebars visible
  }

  if (viewportWidth < BREAKPOINTS.sidebarCompressEnd) {
    // Linear compression from 384px (at 1200px) to 480px (at 1400px)
    const compressionRatio = (viewportWidth - BREAKPOINTS.sidebarCompressStart) /
      (BREAKPOINTS.sidebarCompressEnd - BREAKPOINTS.sidebarCompressStart);
    return Math.round(SIDEBAR_MIN_WIDTH + ((SIDEBAR_MAX_WIDTH - SIDEBAR_MIN_WIDTH) * compressionRatio));
  }

  return SIDEBAR_MAX_WIDTH; // Full width at 1400px+
};

// Front-page News:Events column ratio breakpoint (2026-09-05 brief). Below this viewport width
// the News column (NewsMiniGrid, 240px-min cards) fits only one card-column, so the grid gives
// it the same 1fr weight as Events/Chat/Groups; at/above it, News fits two card-columns and gets
// 2fr. Derived from the live LocalityFrontPage.jsx/GroupFrontPage.jsx grid geometry (300px rail,
// 12px columnGap, 12px container side padding) and NewsMiniGrid's own minmax(240px,1fr)/10px
// gap/2px padding — see docs/briefs/2026-09-05-frontpage-column-ratio-and-responsive-header-type.md
// for the arithmetic. Exported so both front-page files import one value instead of duplicating it.
export const NEWS_DOUBLE_COLUMN_BREAKPOINT = 1607;

/**
 * clampScale - the same floor / linear-interpolate / flat-ceiling shape as calculateSidebarWidth,
 * generalised for any viewport-width-driven scale (e.g. font sizes). Below `start`, returns
 * `minVal`; between `start` and `end`, interpolates linearly; at/above `end`, returns `maxVal`.
 * @param {number} viewportWidth
 * @param {{start: number, end: number, minVal: number, maxVal: number}} range
 * @returns {number}
 */
export const clampScale = (viewportWidth, { start, end, minVal, maxVal }) => {
  if (viewportWidth < start) return minVal;
  if (viewportWidth >= end) return maxVal;
  const ratio = (viewportWidth - start) / (end - start);
  return minVal + (maxVal - minVal) * ratio;
};

/**
 * useMediaQuery - Responsive breakpoint detection hook
 *
 * @param {'feed'|'detail'} pageType - which fixed-chrome shape this page uses on mobile
 *   ('feed': two-row 90px header; 'detail': single-row 50px header — see CHROME above).
 *   Irrelevant on desktop, where chrome is uniform. Defaults to 'feed'.
 * @returns {Object} { isMobile, isTablet, isDesktop, windowWidth, headerHeight, footerHeight }
 *
 * Usage:
 *   const { isMobile, isTablet, isDesktop } = useMediaQuery();
 *   if (isMobile) return <MobileHeader />;
 *
 *   const { headerHeight, footerHeight } = useMediaQuery('detail');
 *   // top: `${headerHeight}px`, bottom: `${footerHeight}px` — correct on every viewport.
 */
export const useMediaQuery = (pageType = 'feed') => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Re-measure synchronously before the first paint. useState already seeds from
  // window.innerWidth, but this closes any first-paint window where the seed could be stale
  // (so the desktop sidebar, gated on isMobile, is never momentarily hidden until a resize).
  // No-op — and no re-render — when the seed is already correct.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    setWindowWidth(prev => (prev === window.innerWidth ? prev : window.innerWidth));
  }, []);

  useEffect(() => {
    // Handle SSR - window may not exist
    if (typeof window === 'undefined') return;

    // Same-value guard (matches the useLayoutEffect above) — without it, every
    // native resize event commits a new render even when width hasn't actually
    // changed. Confirmed as the render-loop root cause (Phase 5a Addendum 2):
    // React's same-value setState bail-out still re-runs the render function
    // before bailing, and a window drag fires dozens of resize events.
    const handleResize = () => {
      setWindowWidth(prev => (prev === window.innerWidth ? prev : window.innerWidth));
    };

    // Use matchMedia for better performance when available
    const mobileQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobile - 1}px)`);
    const tabletQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.mobile}px) and (max-width: ${BREAKPOINTS.tablet - 1}px)`);

    const handleMobileChange = (e) => {
      if (e.matches) setWindowWidth(BREAKPOINTS.mobile - 1);
    };

    const handleTabletChange = (e) => {
      if (e.matches) setWindowWidth(BREAKPOINTS.mobile);
    };

    // Add listeners
    window.addEventListener('resize', handleResize);

    // Modern browsers support addEventListener on MediaQueryList
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleMobileChange);
      tabletQuery.addEventListener('change', handleTabletChange);
    } else {
      // Fallback for older browsers
      mobileQuery.addListener(handleMobileChange);
      tabletQuery.addListener(handleTabletChange);
    }

    // Initial check
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', handleMobileChange);
        tabletQuery.removeEventListener('change', handleTabletChange);
      } else {
        mobileQuery.removeListener(handleMobileChange);
        tabletQuery.removeListener(handleTabletChange);
      }
    };
  }, []);

  const sidebarWidth = calculateSidebarWidth(windowWidth);
  const isSidebarCompact = sidebarWidth > 0 && sidebarWidth < 450;

  // Use mobile interface (headers/footers) when sidebar is not visible
  // This includes both true mobile (<768px) and tablet range (768-1199px)
  const useMobileInterface = windowWidth < BREAKPOINTS.sidebarCompressStart;

  const chrome = !useMobileInterface
    ? CHROME.desktop
    : (pageType === 'detail' ? CHROME.mobileDetail : CHROME.mobileFeed);

  return {
    isMobile: useMobileInterface,  // Use mobile headers/footers when no sidebar
    isTablet: windowWidth >= BREAKPOINTS.mobile && windowWidth < BREAKPOINTS.tablet,
    isDesktop: windowWidth >= BREAKPOINTS.sidebarCompressStart,  // True desktop starts at 1200px
    windowWidth,
    sidebarWidth,        // Dynamic sidebar width (0 for mobile, 384-480 for desktop)
    isSidebarCompact,    // True when sidebar is compressed (<450px)
    headerHeight: chrome.header,  // Fixed HeaderContainer+HeaderButtonsXxxx height for this viewport/pageType
    footerHeight: chrome.footer   // Fixed FooterContainer height for this viewport
  };
};

export default useMediaQuery;
